import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Share2, Check } from 'lucide-react';
import axios from 'axios';
import FileViewer from './FileViewer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Desktop = () => {
  const { id: homeworkId } = useParams(); // Get homework ID from URL
  const navigate = useNavigate();
  
  const [openFile, setOpenFile] = useState(null);
  const [viewerMaximized, setViewerMaximized] = useState(false);
  const [homeworkFiles, setHomeworkFiles] = useState([]);
  const [studentInfo, setStudentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    // Enter fullscreen on load
    const enterFullscreen = async () => {
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        }
      } catch (err) {
        console.log('Fullscreen not supported or blocked:', err);
      }
    };
    enterFullscreen();

    // Fetch data from backend
    fetchData();
  }, []);

  // Auto-open homework if ID is in URL
  useEffect(() => {
    if (homeworkId && homeworkFiles.length > 0) {
      const homework = homeworkFiles.find(hw => hw.id === homeworkId);
      if (homework) {
        setOpenFile(homework);
        setViewerMaximized(false);
      }
    }
  }, [homeworkId, homeworkFiles]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [homeworksRes, studentRes] = await Promise.all([
        axios.get(`${API}/homeworks`),
        axios.get(`${API}/student-info`)
      ]);
      setHomeworkFiles(homeworksRes.data);
      setStudentInfo(studentRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileClick = (file) => {
    setOpenFile(file);
    setViewerMaximized(false);
    // Update URL to include homework ID for sharing
    navigate(`/homework/${file.id}`);
  };

  const handleCloseViewer = () => {
    setOpenFile(null);
    setViewerMaximized(false);
    // Navigate back to home when closing
    navigate('/');
  };

  const handleShareHomework = (file, e) => {
    e.stopPropagation(); // Prevent opening the file
    const shareUrl = `${window.location.origin}/homework/${file.id}`;
    
    // Try to copy to clipboard, handle permission errors gracefully
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        setCopiedId(file.id);
        setTimeout(() => setCopiedId(null), 2000);
      })
      .catch((err) => {
        console.log('Clipboard write failed (may need user gesture):', err);
        // Still show visual feedback even if clipboard fails
        setCopiedId(file.id);
        setTimeout(() => setCopiedId(null), 2000);
      });
  };

  const currentTime = new Date().toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });

  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });

  if (loading || !studentInfo) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-screen overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
      {/* macOS Menu Bar */}
      <div className="absolute top-0 left-0 right-0 h-7 bg-black/40 backdrop-blur-xl border-b border-white/10 flex items-center justify-between px-4 z-50">
        <div className="flex items-center space-x-4 text-white text-sm font-medium">
          <span className="font-semibold">{studentInfo.subject}</span>
        </div>
        <div className="flex items-center space-x-4 text-white text-xs">
          <span>{currentDate}</span>
          <span className="font-medium">{currentTime}</span>
          {/* User Info - macOS style */}
          <div className="flex items-center space-x-2 ml-4 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1 border border-white/20">
            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold">
              {studentInfo.name.split(' ').map(n => n[0]).join('')}
            </div>
            <span className="font-medium">{studentInfo.name}</span>
            <span className="opacity-70">·</span>
            <span className="opacity-90">{studentInfo.matricola}</span>
          </div>
        </div>
      </div>

      {/* Desktop Background */}
      <div className="absolute top-7 left-0 right-0 bottom-0 p-6">
        {/* Desktop Icons */}
        <div className="relative w-full h-full">
          {homeworkFiles.map((file) => (
            <div
              key={file.id}
              className="absolute cursor-pointer group"
              style={{
                left: `${file.position.x}px`,
                top: `${file.position.y}px`
              }}
              onClick={() => handleFileClick(file)}
              data-testid={`homework-icon-${file.id}`}
            >
              <div className="flex flex-col items-center space-y-2 p-3 rounded-lg transition-all duration-200 hover:bg-white/10 backdrop-blur-sm relative">
                <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center text-4xl border border-white/30 shadow-lg group-hover:scale-110 group-hover:bg-white/30 transition-all duration-200">
                  📄
                </div>
                <span className="text-white text-sm font-medium text-center px-2 py-1 bg-black/20 rounded-md backdrop-blur-sm">
                  {file.name}
                </span>
                
                {/* Share Button */}
                <button
                  onClick={(e) => handleShareHomework(file, e)}
                  className="absolute -top-2 -right-2 w-8 h-8 bg-blue-500 hover:bg-blue-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-lg"
                  title="Share homework link"
                  data-testid={`share-button-${file.id}`}
                >
                  {copiedId === file.id ? (
                    <Check className="w-4 h-4 text-white" />
                  ) : (
                    <Share2 className="w-4 h-4 text-white" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* File Viewer Window */}
      {openFile && (
        <FileViewer
          file={openFile}
          onClose={handleCloseViewer}
          isMaximized={viewerMaximized}
          onToggleMaximize={() => setViewerMaximized(!viewerMaximized)}
        />
      )}
    </div>
  );
};

export default Desktop;
